import { useCallback, useMemo, useState } from 'react';
import { DataGrid } from 'src/feature/data-grid/data-grid';
import { useDataGridSearchParams } from 'src/feature/data-grid/use-data-grid-search-params/use-data-grid-search-params';
import { Snackbar } from 'src/shared/snackbar/snackbar';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_DATA_GRID_MODEL,
  DEFAULT_PAGE_SIZE,
} from 'src/config/data-grid.config';
import { useGetLogsPaginatedQuery } from '@service/log.service';
import { DataGridQueryModel } from '@model/data-grid.model';
import { SortOrder } from '@delmonicos/shared-sdk/libs/emsp/src/libs/core/src/utils/mongo/pagination/models/pagination.model';
import { GetLogsPaginatedModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/logger-service/models/get-logs-paginated.model';
import { useGenericLogColumn } from './column/use-generic-log-column';
import { useOcpiLogColumn } from './column/use-ocpi-log-column';
import { useEmspWebAppLogColumn } from './column/use-emsp-web-app-log-column';
import { useOcpiLogFilter } from './filter/use-ocpi-log-filter';
import { useGenericLogFilter } from './filter/use-generic-log-filter';

export function LogDataGrid(): React.JSX.Element {
  const { t } = useTranslation();

  const genericLogColumn = useGenericLogColumn();
  const ocpiLogColumn = useOcpiLogColumn();
  const emspWebAppLogColumn = useEmspWebAppLogColumn();

  const columns = useMemo(
    () => [...genericLogColumn, ...ocpiLogColumn, ...emspWebAppLogColumn],
    [genericLogColumn, ocpiLogColumn, emspWebAppLogColumn],
  );

  // PAGINATION & FILTERS //////////////////////////////////////////////////////

  const ocpiLogFilter = useOcpiLogFilter();
  const genericLogFilter = useGenericLogFilter();

  const filterDefinitionModel = [...ocpiLogFilter, ...genericLogFilter];

  const { model: urlModel } = useDataGridSearchParams(DEFAULT_DATA_GRID_MODEL);
  const [model, setModel] = useState<DataGridQueryModel>(urlModel);

  const [params, setParams] = useState<GetLogsPaginatedModel>(() => {
    const pageSize =
      DEFAULT_DATA_GRID_MODEL.paginate?.pageSize ?? DEFAULT_PAGE_SIZE;
    const page = DEFAULT_DATA_GRID_MODEL.paginate?.page ?? 0;

    return {
      sortField: 'eventDate',
      sortOrder: SortOrder.DESC,
      limit: pageSize,
      offset: page * pageSize,
      textSearch: urlModel.textSearch || undefined,
      searchOnEmspWebAppLogs: true,
      searchOnOcpiLogs: true,
      ocpiFilter: {},
    };
  });

  const onModelChange = useCallback(
    (newModel: DataGridQueryModel) => {
      setModel(newModel);

      setParams((prev) => {
        const pageSize =
          newModel.paginate?.pageSize ?? prev.limit ?? DEFAULT_PAGE_SIZE;
        const page = newModel.paginate?.page ?? 0;

        return {
          ...prev,
          limit: pageSize,
          offset: page * pageSize,
          textSearch: newModel.textSearch || undefined,
          searchOnEmspWebAppLogs: true,
          searchOnOcpiLogs: true,
          ocpiFilter: {}, // TODO: map filters from model to ocpiFilter and emspWebAppFilter
        };
      });
    },

    [setModel],
  );

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const {
    data: paginatedData,
    isFetching,
    isError,
  } = useGetLogsPaginatedQuery(params);

  return (
    <>
      <DataGrid
        getRowId={(row) => {
          return row.eventId;
        }}
        columns={columns}
        filterDefinitionModel={filterDefinitionModel}
        loading={isFetching}
        rows={paginatedData?.docs ?? []}
        rowCount={paginatedData?.totalDocs ?? 0}
        model={model}
        onModelChange={onModelChange}
      />

      {isError && (
        <Snackbar variant="error" message={t('error.get-list-log-query')} />
      )}
    </>
  );
}
